import React, { memo } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.guardianCircle;

type Props = {
  /** Player's first name, woven into the title/message copy. */
  firstName: string;
  /** Invite CTA is primary-guardian-only (same gate as the sidebar invite button). */
  isPrimaryGuardian: boolean;
  onInvite: () => void;
};

/**
 * Right-pane empty state (tablet/web) shown when the circle has no co-guardians yet.
 * Primary guardians get an Invite CTA; co-guardians only see the copy.
 */
function GuardianCircleEmptyState({ firstName, isPrimaryGuardian, onInvite }: Props) {
  const styles = useStyles();
  const { scale } = useScaling();

  const message = strings.emptyMessage.replace('{name}', firstName);

  return (
    <Stack flex={1} align="center" justify="center" paddingX={scale(24)} gap={scale(12)}>
      <Text variant="Font Guardian App/Heading/Heading H5 24px" style={styles.title}>
        {strings.emptyTitle}
      </Text>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.message}>
        {message}
      </Text>
      {isPrimaryGuardian ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={strings.emptyInvite}
          onPress={onInvite}
          style={({ pressed }) => [styles.button, pressed ? styles.pressed : null]}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            style={styles.buttonLabel}>
            {strings.emptyInvite}
          </Text>
        </Pressable>
      ) : null}
    </Stack>
  );
}

export default memo(GuardianCircleEmptyState);

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: guardianColors['10'],
    textAlign: 'center',
  },
  message: {
    color: guardianColors['6'],
    textAlign: 'center',
    maxWidth: scale(360),
  },
  button: {
    marginTop: scale(12),
    paddingHorizontal: scale(24),
    height: scale(48),
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: guardianColors['Main/10'],
  },
  buttonLabel: {
    color: guardianColors['Main/Main'],
  },
  pressed: {
    opacity: 0.7,
  },
}));
